import { Component, ElementRef, Injector, Input, ViewChild, OnChanges } from '@angular/core';
import { BaseFormComponent } from '../base-form.component';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import * as ld from 'lodash';
import { Product } from '../../../../../../common/js/static/Product';
import { Subscription } from 'rxjs';
const scrollHeight: number = 24;

@Component({
  moduleId: module.id,
  selector: 'select-basic',
  host: {
    '(document:click)': 'handleClick($event)',
  },
  templateUrl: './select-basic.component.html',
})
export class SelectBasicComponent extends BaseFormComponent implements OnChanges {
  options: Array<any>;
  selectedItem: string;
  public selectedIndex: number = 0;
  componentId: string;
  showDropdown: boolean = false;
  elementRef: ElementRef;
  autoCompleteShelf: '';
  lastAutoCompleteTime: number;
  RESET_DELAY: number = 750;
  stateChangeSubscription: Subscription;

  @Input() property: string;
  @Input() placeholder: string;
  @Input() showTitle: boolean;
  @Input() isDriversLicense: boolean = false;
  @Input() label: string;

  // disableConditionally allows users to force the component to be disabled, just as is the case for select-basic.
  // However, if not set, disable logic will be delegated to the super class and the isDisabled boolean
  @Input() disableConditionally: boolean = false;
  @Input() valueOptions?: Array<object>;

  @ViewChild('dropdownUl') dropdown: ElementRef;
  @ViewChild('selectInput') inputBox: ElementRef;

  constructor(protected injector: Injector, elementRef: ElementRef, private amorphicService: AmorphicService) {
    super(injector);
    this.elementRef = elementRef;
    this.showTitle = true;
    this.lastAutoCompleteTime = Date.now();

    if (this.prop === 'customer.application.primaryPolicy.insuredType') {
      this.stateChangeSubscription = this.geoChangeService.geoChangeSource$.subscribe((event) => {
        this.handleGeoChange(event);
      });
    }
  }

  ngOnChanges() {}

  private incrementSelectedIndex() {
    this.selectedIndex = this.selectedIndex < this.options.length - 1 ? this.selectedIndex + 1 : this.selectedIndex;
  }

  private decrementSelectedIndex() {
    this.selectedIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : this.selectedIndex;
  }

  keyUpPress(event: KeyboardEvent): void | boolean {
    if (event.code === 'Tab') {
      this.showBox();
    }
  }

  keyDownPress(event: KeyboardEvent): void | boolean {
    if (event.code === 'Tab') {
      this.hideBox();
      this.autoCompleteShelf = '';
      return true;
    }
    this.handleKeyInteractions(event);
    return false;
  }

  handleClick(event: MouseEvent) {
    const clickedComponent = event.target;
    let inside = false;

    if (this.disableConditionally) {
      return true;
    }

    if (this.isDisabled) {
      return true;
    }

    if (clickedComponent === this.inputBox.nativeElement) {
      this.toggleBox();
      return false;
    }

    if (!this.dropdown) {
      return true;
    }

    if (clickedComponent === this.dropdown.nativeElement) {
      inside = true;
    }

    const parentNode = (<HTMLElement>clickedComponent).parentNode;
    if (parentNode && parentNode === this.dropdown.nativeElement) {
      inside = true;
    }

    if (!inside) {
      this.hideBox();
      return false;
    }
    return false;
  }

  /*
        first touch router of key events.
     */
  handleKeyInteractions(event: KeyboardEvent) {
    const code = event.code;
    const key = event.key;
    this.keyUpPress(event);
    if (code === 'Enter' || code === 'Space') {
      this.trigger(this.options[this.selectedIndex]);
    } else if (code === 'Escape') {
      this.hideBox();
    } else if (code === 'ArrowUp') {
      const options = { block: 'nearest', inline: 'start' };
      const index = this.selectedIndex - 1 < 0 ? 0 : this.selectedIndex - 1;
      const option = this.dropdown.nativeElement.children[index];
      option.scrollIntoView(options);
      this.decrementSelectedIndex();
    } else if (code === 'ArrowDown') {
      this.incrementSelectedIndex();
      const options = { block: 'nearest', inline: 'end' };
      const option = this.dropdown.nativeElement.children[this.selectedIndex];
      option.scrollIntoView(options);
    } else if (key.length === 1 && /[-_ A-Za-z0-9]/.test(key)) {
      if (Date.now() - this.lastAutoCompleteTime > this.RESET_DELAY) {
        this.autoCompleteShelf = '';
      }
      this.lastAutoCompleteTime = Date.now();
      this.autoCompleteShelf += key;
      let opt;
      const options = { block: 'nearest', inline: 'start' };
      // this regex looks for any whitespace followed by the current shelf (string)
      // starting from the begining of the state which is the content of the li element
      const re = new RegExp(`^\\s*${this.autoCompleteShelf}`, 'i');
      let index = -1;
      for (let i = 0, l = this.dropdown.nativeElement.children.length; i < l; i++) {
        opt = this.dropdown.nativeElement.children[i];
        if (re.test(opt.textContent)) {
          index = i;
          break;
        }
      }
      if (index !== -1) {
        this.selectedIndex = index;
        opt.scrollIntoView(options);
      }
    }
    return false;
  }

  private resetSelectedIndex() {
    this.selectedIndex = 0;
  }

  ngOnInit() {
    if (!this.property) {
      this.property = this.questionItem.prop;
      this.property = this.handleIterate(this.property);
    }
    this.componentId = this.getComponentId();
    this.updateFromModel(); // For the initial value

    //if an option is removed from a property and database still holds value which is already removed, it resets the component value and also database
    //do it only if there is responses allowed, as it signifies that option was removed recently
    const selectedItem = this.selectedItem;
    if (
      this.questionItem.responsesAllowed &&
      this.options &&
      this.options.filter((option) => option.description === selectedItem).length === 0
    ) {
      this.resetValue();
      this.resetNode();
    }

    // multi-control-splitter gives a placeholder
    // otherwise comes directly from metadata
    if (!this.placeholder && this.questionItem.controlType !== 'multi-controls-splitter') {
      this.placeholder = this.questionItem.placeholder;
    }
  }

  setOptions() {
    // use options that are passed in as an input before checking
    // the property decorator and metadata values
    if (this.valueOptions !== undefined && this.valueOptions.length > 0) {
      this.options = this.valueOptions;
      return;
    }

    const values = this.getValuesFor(this.property);
    const descriptionsObj = this.getDescriptionsFor(this.property);
    this.options = [];

    // This featureFlags code is currently only excercised to control showing only
    // those plans (100, 65, 20, 15, 12, 10) that are enabled in launchdarkly
    // Because of how the code is structured we are reading exact flag names from two places
    // CoverpathTypes FeatureFlags and feature_flags from App questions JSON
    // They need to be consistent.
    const featureFlags: Array<{ [key: string]: string }> = this.questionItem.featureFlags || [];
    const controller = this.amorphicService.controller;

    //use descriptions incase if encountering descriptions as well
    if (descriptionsObj) {
      ld.map(
        values,
        function (key) {
          const description: string =
            typeof descriptionsObj[key.toString()] === 'object'
              ? descriptionsObj[key.toString()].text
              : descriptionsObj[key.toString()];

          if (description) {
            this.options.push({
              value: key,
              description: this.parsePredicates(description),
            });
          }
        }.bind(this)
      );
    } else {
      /*
            Values could be of two types
            1) array
            2) object
            * */

      if (values instanceof Array) {
        values.forEach((value) => {
          this.options.push({
            value: value,
            description: value,
          });
        });
      } else {
        ld.map(
          values,
          function (description, key) {
            this.options.push({
              value: key,
              description: this.parsePredicates(description),
            });
          }.bind(this)
        );
      }
    }
  }

  updateFromModel() {
    // check for special cases
    if (!this.shouldDisplayChangedValue()) {
      return;
    }

    this.cdRef.detectChanges();
    this.setOptions();
    const value = this.getValue(this.property);
    let valueStr = '';
    if (value !== null && typeof value !== 'undefined') {
      valueStr = value + '';
    }
    const option = this.options.find((opt) => opt.value === valueStr);
    this.selectedItem = option && option.description ? option.description : valueStr;
  }

  resetValue() {
    this.selectedItem = '';
  }

  async trigger(option) {
    this.selectedItem = option.description;
    this.resetSelectedIndex();
    this.hideBox();

    if (this.prop.includes('insuredType') && this.selectedItem === 'You') {
      // check Insured's state against the "application" state
      const insuredState = this.amorphicService.controller.customer.application.primaryPolicy.insured.address.state;
      const applicationState = this.amorphicService.controller.customer.application.primaryPolicy.getApplicationState();
      const oldInsuredTypeValue = this.amorphicService.controller.customer.application.primaryPolicy.insuredType;

      if (await this.unsupportedUseCasesService.isUnsupportedUseCase('insuredState', insuredState)) {
        this.appSvc.markPropertyAsChanged(this.prop);
        return;
      }

      this.geoChangeService.changeBasedOnStateIfNecessary(
        this.property,
        insuredState,
        applicationState,
        oldInsuredTypeValue,
        option.value,
        'application-form'
      );
    } else {
      await this.validateAndSet(this.property, option.value);
    }
  }

  handleBlur() {
    if (!this.showDropdown && !this.errorMessage[this.prop]) {
      const option = this.options.find((option) => option.description === this.selectedItem);
      const optionVal = (option || {}).value;
      this.checkEmptyAndValidate(this.property, optionVal);
    }
  }

  isError(): boolean {
    return !!this.errorMessage[this.property];
  }

  getErrorMessage(): string {
    return this.errorMessage[this.property];
  }

  getClasses() {
    const error = this.isError();
    const isComplete = !error && !!this.selectedItem && this.selectedItem !== 'null';
    return {
      complete: isComplete,
      error: error,
    };
  }

  showBox(): void {
    this.showDropdown = true;
  }

  toggleBox(): void {
    this.showDropdown = !this.showDropdown;
  }

  hideBox(): void {
    this.showDropdown = false;
  }

  public getComponentId() {
    // this.property -> involves customProp when the question has more than one binding
    return this.questionItem.id + '_' + this.property;
  }

  shouldDisplayChangedValue(): boolean {
    // drivers license id number and passport/state issued id numbers are bound to the same property
    // do not display id number on driver's license input if no drivers license is checked
    const hasDriversLicense =
      this.amorphicService.controller.customer.application.primaryPolicy.insured.person.hasDriversLicense;
    if (this.isDriversLicense && !hasDriversLicense) {
      return false;
    }

    return true;
  }

  private async handleGeoChange(response): Promise<void> {
    if (response.prop === this.prop) {
      if (response.event === 'propValueChanged') {
        await this.validateAndSet(this.property, response.value);
      } else if (response.event === 'propValueKept') {
        // reset to old prop value
        this.selectedItem = this.options.find((opt) => opt.value === response.value).description;
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.stateChangeSubscription) {
      this.stateChangeSubscription.unsubscribe();
    }
  }
}
